/**
 * applyThemeGroupsToScreens
 *
 * Pure engine for selectively applying theme attribute groups
 * (Background / Header / Footer / Body) to live canvas screens.
 *
 * React-free, mutation-free, persistence-free: returns new screen objects and
 * a classification per screen so callers can render a confirm dialog and
 * decide what to persist.
 *
 * Classification (computed at apply time, no persisted dirty-tracking):
 * - result content equals current content        → no-op   (skippedNoop)
 * - applied-region paths equal factory default   → fresh   (apply quietly)
 * - applied-region paths differ from both        → customized (confirm with ⚠️)
 *
 * @module themes/mappers
 */

import type { StepKind, StepNode } from '@segmently/schema-models/v2';
import type { ThemeV2 } from '../types/themesV2.types';
import { defaultThemeV2Mapper } from './DefaultThemeV2Mapper';
import { getMappingGroup, supportedGroups, type ThemeApplyGroup } from './themeApplyGroups';
import { createDefaultStepNode } from '../factories/defaultStepNodeFactory';
import { getValueByPath, setValueByPath, deepEqual } from './objectPath';
import { kindSubtypeKey } from './templateIdentity';
import { resolveEntranceForKind } from '../animation/applyAnimationSettingsToFlowDocument';

/**
 * Motion-related top-level content keys the Animations group copies verbatim
 * from the template (absence propagates). Per-entity animation fields
 * (options.itemAnimation, button enter/idle/tap) are DEFERRED v1 scope and are
 * intentionally not part of this group.
 */
const ANIMATION_CONTENT_KEYS = ['screenAnimation', 'animated', 'elementAnimations'] as const;

export type ThemeApplyClassification = 'fresh' | 'customized';

/** Minimal screen shape the engine needs — works for StepNode and FlowStepNode alike. */
export type ThemeApplyScreenLike = Pick<StepNode, 'screenType' | 'content' | 'subtype'>;

export interface ThemeApplyScreenChange<T extends ThemeApplyScreenLike> {
  /** Original screen, untouched. */
  before: T;
  /** New screen object with the requested groups applied. */
  after: T;
  classification: ThemeApplyClassification;
  /** Requested groups that this StepKind supports and that were applied. */
  appliedGroups: ThemeApplyGroup[];
  /** Requested groups this StepKind does not support (not applied). */
  clippedGroups: ThemeApplyGroup[];
}

export interface ApplyThemeGroupsResult<T extends ThemeApplyScreenLike> {
  changed: ThemeApplyScreenChange<T>[];
  /** Screens already equal to the theme for the requested groups. */
  skippedNoop: T[];
  /** Screens whose StepKind supports none of the requested groups. */
  unsupported: { screen: T; requestedGroups: ThemeApplyGroup[] }[];
}

export interface ApplyThemeGroupsOptions {
  /**
   * Per-kind template id preference. Set when the user clicked a specific
   * preview tile ("Apply for this type") so duplicate-kind themes apply the
   * tile that was actually clicked. Without it, the first template of the
   * kind in flowDocument order is used (matches carousel display order).
   */
  templateIdByKind?: Record<string, string>;
}

/**
 * Resolve the TEMPLATE screen for a given canvas screen — the source of truth
 * for apply. The preview tile renders exactly this screen, so "apply" must make
 * the canvas region look like the tile (WYSIWYG), including removals the user
 * made on the template (members set to null / regions deleted).
 *
 * Templates are keyed by `(screenType, subtype)` — a theme can hold several
 * templates of the same StepKind distinguished only by subtype, so resolution
 * matches the screen's OWN subtype, never first-of-kind. `undefined`/`''`
 * subtypes share the "no-subtype" bucket, so legacy themes resolve unchanged.
 *
 * `preferredId` (the clicked "Apply for this type" tile) wins ONLY when its
 * identity matches the screen's — it never drags a quiz tile onto a feedback
 * screen.
 */
function resolveTemplateForScreen(
  theme: ThemeV2,
  kind: StepKind,
  subtype: string | undefined,
  preferredId?: string
): ThemeApplyScreenLike | undefined {
  const screens = theme.flowDocument?.screens ?? {};
  const wantKey = kindSubtypeKey(kind, subtype);
  if (preferredId) {
    const preferred = screens[preferredId];
    if (
      preferred &&
      kindSubtypeKey(preferred.screenType as StepKind, preferred.subtype) === wantKey
    ) {
      return preferred;
    }
  }
  for (const id of Object.keys(screens)) {
    const candidate = screens[id];
    if (
      candidate &&
      kindSubtypeKey(candidate.screenType as StepKind, candidate.subtype) === wantKey
    ) {
      return candidate;
    }
  }
  return undefined;
}

/** Does the theme hold ANY template of this StepKind (regardless of subtype)? */
function themeHasKind(theme: ThemeV2, kind: StepKind): boolean {
  const screens = theme.flowDocument?.screens ?? {};
  return Object.keys(screens).some((id) => screens[id]?.screenType === kind);
}

function regionValuesAtPath(content: unknown, target: string): unknown[] {
  const arrayMatch = target.match(/^(.+)\[\]\.(.+)$/);
  if (!arrayMatch) return [getValueByPath(content, target)];
  const [, arrayPath, itemPath] = arrayMatch;
  const array = getValueByPath(content, arrayPath);
  if (!Array.isArray(array)) return [];
  return array.map((item) => getValueByPath(item, itemPath));
}

/**
 * Compare the screen's current values at a mapping target path against the
 * factory default. Array paths compare every current item against the factory
 * first-item value (factory defaults are uniform across items).
 */
function pathEqualsFactoryDefault(
  currentContent: unknown,
  factoryContent: unknown,
  target: string
): boolean {
  const factoryReference = regionValuesAtPath(factoryContent, target)[0];
  return regionValuesAtPath(currentContent, target).every((value) =>
    deepEqual(value, factoryReference)
  );
}

function classifyScreen(
  screen: ThemeApplyScreenLike,
  appliedGroups: ThemeApplyGroup[]
): ThemeApplyClassification {
  const factory = createDefaultStepNode(screen.screenType as StepKind);
  const content = screen.content as unknown as Record<string, unknown>;
  const factoryContent = factory.content as unknown as Record<string, unknown>;

  for (const group of appliedGroups) {
    if (group === 'background') {
      if (!deepEqual(getValueByPath(content, 'canvas.background'), getValueByPath(factoryContent, 'canvas.background'))) {
        return 'customized';
      }
      if (!deepEqual(
        getValueByPath(content, 'canvas.webViewBackgroundColor'),
        getValueByPath(factoryContent, 'canvas.webViewBackgroundColor')
      )) {
        return 'customized';
      }
    } else if (group === 'header') {
      if (!deepEqual(content.header, factoryContent.header)) return 'customized';
    } else if (group === 'footer') {
      if (!deepEqual(content.actionBar, factoryContent.actionBar)) return 'customized';
    } else if (group === 'animations') {
      for (const key of ANIMATION_CONTENT_KEYS) {
        if (!deepEqual(content[key], factoryContent[key])) return 'customized';
      }
    } else {
      const bodyMappings = defaultThemeV2Mapper
        .getMappingsForStepKind(screen.screenType as StepKind)
        .filter((mapping) => getMappingGroup(mapping) === 'body');
      for (const mapping of bodyMappings) {
        if (!pathEqualsFactoryDefault(content, factoryContent, mapping.target)) {
          return 'customized';
        }
      }
    }
  }
  return 'fresh';
}

/**
 * Apply the requested theme groups to each screen.
 *
 * Source of truth: the per-type TEMPLATE screen from `theme.flowDocument`
 * (exactly what the preview tile renders). Structural groups copy the
 * template region verbatim — null members and deleted regions propagate, so
 * removals made on the template reach the canvas. Body copies the values at
 * each body mapping target from the template (already transformed).
 *
 * Fallback (theme has no template of that kind): legacy behavior — token
 * mappings from theme + undefined-guarded structural overrides from the
 * global theme.background/headerBar/actionBar fields.
 */
export function applyThemeGroupsToScreens<T extends ThemeApplyScreenLike>(
  theme: ThemeV2,
  groups: ThemeApplyGroup[],
  screens: T[],
  opts?: ApplyThemeGroupsOptions
): ApplyThemeGroupsResult<T> {
  const result: ApplyThemeGroupsResult<T> = {
    changed: [],
    skippedNoop: [],
    unsupported: [],
  };

  for (const screen of screens) {
    const kind = screen.screenType as StepKind;
    const supported = supportedGroups(kind);
    const appliedGroups = groups.filter((group) => supported.includes(group));
    const clippedGroups = groups.filter((group) => !supported.includes(group));

    if (appliedGroups.length === 0) {
      result.unsupported.push({ screen, requestedGroups: [...groups] });
      continue;
    }

    const template = resolveTemplateForScreen(
      theme,
      kind,
      screen.subtype,
      opts?.templateIdByKind?.[kind]
    );

    // Subtype miss: the theme HAS template(s) of this kind, but none matches the
    // screen's (kind, subtype). Strict routing → leave the screen unchanged
    // (counts as Skipped). The legacy token fallback below runs ONLY when the
    // theme has no template of this kind at all (older/partial themes), so
    // no-subtype themes behave exactly as before.
    if (!template && themeHasKind(theme, kind)) {
      result.skippedNoop.push(screen);
      continue;
    }

    const after = structuredClone(screen);
    const afterContent = after.content as unknown as Record<string, unknown>;

    if (template) {
      const templateContent = template.content as unknown as Record<string, unknown>;

      // Structural regions: verbatim copy from the template, absence included.
      const copyRegion = (regionKey: 'header' | 'actionBar') => {
        if (templateContent[regionKey] !== undefined) {
          afterContent[regionKey] = structuredClone(templateContent[regionKey]);
        } else {
          delete afterContent[regionKey];
        }
      };
      if (appliedGroups.includes('background')) {
        const templateBackground = getValueByPath(templateContent, 'canvas.background');
        const templateChromeColor = getValueByPath(
          templateContent,
          'canvas.webViewBackgroundColor'
        );
        const canvas = (afterContent.canvas ?? {}) as Record<string, unknown>;
        if (templateBackground !== undefined) {
          canvas.background = structuredClone(templateBackground);
        } else {
          delete canvas.background;
        }
        if (templateChromeColor !== undefined) {
          canvas.webViewBackgroundColor = structuredClone(templateChromeColor);
        } else {
          delete canvas.webViewBackgroundColor;
        }
        afterContent.canvas = canvas;
      }
      if (appliedGroups.includes('header')) copyRegion('header');
      if (appliedGroups.includes('footer')) copyRegion('actionBar');

      // Animations: verbatim copy of the motion keys from the template
      // (tile = WYSIWYG — including "no entrance" when the template has none).
      if (appliedGroups.includes('animations')) {
        for (const key of ANIMATION_CONTENT_KEYS) {
          if (templateContent[key] !== undefined) {
            afterContent[key] = structuredClone(templateContent[key]);
          } else {
            delete afterContent[key];
          }
        }
      }

      // Body: transfer the template's value at every body mapping target.
      // Values are already transformed (e.g. 50%-alpha subtitles) — no
      // re-deriving from theme tokens. Array targets use the template's
      // first-item value for every canvas item (factory defaults are uniform).
      if (appliedGroups.includes('body')) {
        const bodyMappings = defaultThemeV2Mapper
          .getMappingsForStepKind(kind)
          .filter((mapping) => getMappingGroup(mapping) === 'body');
        for (const mapping of bodyMappings) {
          const arrayMatch = mapping.target.match(/^(.+)\[\]\.(.+)$/);
          if (!arrayMatch) {
            const value = getValueByPath(templateContent, mapping.target);
            if (value !== undefined) {
              setValueByPath(afterContent, mapping.target, structuredClone(value));
            }
            continue;
          }
          const [, arrayPath, itemPath] = arrayMatch;
          const templateValue = regionValuesAtPath(templateContent, mapping.target)[0];
          if (templateValue === undefined) continue;
          const items = getValueByPath(afterContent, arrayPath);
          if (!Array.isArray(items)) continue;
          for (const item of items) {
            if (item && typeof item === 'object') {
              setValueByPath(
                item as Record<string, unknown>,
                itemPath,
                structuredClone(templateValue)
              );
            }
          }
        }
      }
    } else {
      // Fallback: no template of this kind in the theme — legacy behavior.
      after.content = defaultThemeV2Mapper.applyToStepContent(
        theme,
        kind,
        after.content,
        appliedGroups
      );
      const fallbackContent = after.content as unknown as Record<string, unknown>;
      if (appliedGroups.includes('background') && theme.background !== undefined) {
        const canvas = (fallbackContent.canvas ?? {}) as Record<string, unknown>;
        canvas.background = structuredClone(theme.background);
        if (typeof theme.webViewBackgroundColor === 'string') {
          canvas.webViewBackgroundColor = theme.webViewBackgroundColor;
        } else if (theme.webViewBackgroundColor === null) {
          delete canvas.webViewBackgroundColor;
        }
        fallbackContent.canvas = canvas;
      }
      if (appliedGroups.includes('header') && theme.headerBar !== undefined) {
        fallbackContent.header = structuredClone(theme.headerBar);
      }
      if (appliedGroups.includes('footer') && theme.actionBar !== undefined) {
        fallbackContent.actionBar = structuredClone(theme.actionBar);
      }
      // Animations fallback: derive from theme.animationSettings (per-kind
      // override aware). Absent settings → screen motion left untouched.
      if (appliedGroups.includes('animations') && theme.animationSettings) {
        const entrance = resolveEntranceForKind(theme.animationSettings, kind);
        if (entrance === null) {
          delete fallbackContent.screenAnimation;
          fallbackContent.animated = false;
        } else if (entrance !== undefined) {
          fallbackContent.screenAnimation = structuredClone(entrance);
          fallbackContent.animated = true;
        }
      }
    }

    if (deepEqual(after.content, screen.content)) {
      result.skippedNoop.push(screen);
      continue;
    }

    result.changed.push({
      before: screen,
      after,
      classification: classifyScreen(screen, appliedGroups),
      appliedGroups,
      clippedGroups,
    });
  }

  return result;
}
